import { useCallback, useRef, useState } from 'react';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { isSystemVariable, makeVariablePattern, normalizeVariableLabel } from '../TemplateVariables.utils';
import { useHasTemplateVariablesProvider, useTemplateVariables } from '../TemplateVariablesContext';
import { useInsertVariableIntoTextField } from './useInsertVariableIntoTextField';
import { useTextFieldKeyboardTrigger } from './useTextFieldKeyboardTrigger';
import { useVariablePicker } from './useVariablePicker';
export function useTextFieldVariableIntegration(inputRef, setValue) {
    let config = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {};
    var _config_showDefaultValuePopover, _ref;
    const { disabled, disableVariablePicker, excludeLabels, adornmentStyle } = config;
    const showDefaultValuePopover = (_config_showDefaultValuePopover = config.showDefaultValuePopover) !== null && _config_showDefaultValuePopover !== void 0 ? _config_showDefaultValuePopover : true;
    const { TEMPLATE_VARIABLES } = useFeatureFlags();
    const hasProvider = useHasTemplateVariablesProvider();
    const { customVariables, resolvedValues, templateVariableValues, setTemplateVariableValue } = useTemplateVariables();
    const { pickerState, openPicker, closePicker } = useVariablePicker();
    const adornmentRef = useRef(null);
    const [pendingDefaultValue, setPendingDefaultValue] = useState(null);
    const handleInsertVariable = useInsertVariableIntoTextField(inputRef, setValue);
    const handleKeyDown = useTextFieldKeyboardTrigger(inputRef, setValue, openPicker, handleInsertVariable);
    const enabled = !disableVariablePicker && !!TEMPLATE_VARIABLES;
    const handleInputClick = useCallback(()=>{
        var _input_selectionStart;
        const input = inputRef.current;
        if (!input) return;
        const cursorPos = (_input_selectionStart = input.selectionStart) !== null && _input_selectionStart !== void 0 ? _input_selectionStart : 0;
        const value = input.value;
        for (const match of value.matchAll(makeVariablePattern())){
            const start = match.index;
            const end = start + match[0].length;
            if (cursorPos >= start && cursorPos <= end) {
                input.setSelectionRange(start, end);
                const label = match[1].trim();
                if (showDefaultValuePopover && hasProvider && !isSystemVariable(label)) {
                    setPendingDefaultValue({
                        label,
                        anchorEl: input
                    });
                }
                return;
            }
        }
    }, [
        inputRef,
        hasProvider,
        showDefaultValuePopover
    ]);
    const handleArrowKeySkip = useCallback((event)=>{
        var _input_selectionStart;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const input = inputRef.current;
        if (!input) return;
        const cursorPos = (_input_selectionStart = input.selectionStart) !== null && _input_selectionStart !== void 0 ? _input_selectionStart : 0;
        const value = input.value;
        const isShift = event.shiftKey;
        for (const match of value.matchAll(makeVariablePattern())){
            const start = match.index;
            const end = start + match[0].length;
            if (event.key === 'ArrowRight' && cursorPos >= start && cursorPos < end) {
                event.preventDefault();
                if (isShift) {
                    input.setSelectionRange(input.selectionStart, end, 'forward');
                } else {
                    input.setSelectionRange(end, end);
                }
                return;
            }
            if (event.key === 'ArrowLeft' && cursorPos > start && cursorPos <= end) {
                event.preventDefault();
                if (isShift) {
                    input.setSelectionRange(start, input.selectionEnd, 'backward');
                } else {
                    input.setSelectionRange(start, start);
                }
                return;
            }
        }
    }, [
        inputRef
    ]);
    const combinedKeyDown = useCallback((event)=>{
        handleArrowKeySkip(event);
        if (!event.defaultPrevented) {
            handleKeyDown(event);
        }
    }, [
        handleArrowKeySkip,
        handleKeyDown
    ]);
    const inputProps = enabled ? {
        onKeyDown: combinedKeyDown,
        onClick: handleInputClick
    } : undefined;
    const handleAdornmentClick = useCallback(()=>{
        if (adornmentRef.current) {
            openPicker(adornmentRef.current, handleInsertVariable);
        }
    }, [
        openPicker,
        handleInsertVariable
    ]);
    const handleSelect = useCallback((label)=>{
        var _pickerState_anchorEl;
        var _pickerState_onInsert;
        const anchor = (_pickerState_anchorEl = pickerState.anchorEl) !== null && _pickerState_anchorEl !== void 0 ? _pickerState_anchorEl : adornmentRef.current;
        (_pickerState_onInsert = pickerState.onInsert) === null || _pickerState_onInsert === void 0 ? void 0 : _pickerState_onInsert.call(pickerState, label);
        closePicker();
        const hasExistingValue = !!templateVariableValues[normalizeVariableLabel(label)];
        if (showDefaultValuePopover && hasProvider && !isSystemVariable(label) && !hasExistingValue) {
            setPendingDefaultValue({
                label,
                anchorEl: anchor
            });
        }
    }, [
        pickerState,
        closePicker,
        hasProvider,
        showDefaultValuePopover,
        templateVariableValues
    ]);
    const handleClose = useCallback(()=>{
        var _pickerState_onDismiss;
        (_pickerState_onDismiss = pickerState.onDismiss) === null || _pickerState_onDismiss === void 0 ? void 0 : _pickerState_onDismiss.call(pickerState);
        closePicker();
    }, [
        pickerState,
        closePicker
    ]);
    const handleDefaultValueConfirm = useCallback((value)=>{
        if (pendingDefaultValue) {
            var _resolvedValues_normalized;
            const normalized = normalizeVariableLabel(pendingDefaultValue.label);
            const trimmedResolved = ((_resolvedValues_normalized = resolvedValues[normalized]) !== null && _resolvedValues_normalized !== void 0 ? _resolvedValues_normalized : '').trim();
            if (value !== trimmedResolved) {
                setTemplateVariableValue(pendingDefaultValue.label, value);
            }
        }
        setPendingDefaultValue(null);
    }, [
        pendingDefaultValue,
        resolvedValues,
        setTemplateVariableValue
    ]);
    const handleDefaultValueDismiss = useCallback(()=>{
        setPendingDefaultValue(null);
    }, []);
    return {
        enabled,
        inputProps,
        adornmentRef,
        adornmentStyle,
        fieldAdornmentProps: {
            onClick: handleAdornmentClick,
            disabled
        },
        resolvedValues,
        pickerPopoverProps: {
            anchorEl: pickerState.anchorEl,
            customTags: {
                variables: customVariables
            },
            excludeLabels,
            onSelect: handleSelect,
            onClose: handleClose
        },
        defaultValuePopoverProps: {
            anchorEl: (_ref = pendingDefaultValue === null || pendingDefaultValue === void 0 ? void 0 : pendingDefaultValue.anchorEl) !== null && _ref !== void 0 ? _ref : null,
            initialValue: pendingDefaultValue ? resolvedValues[normalizeVariableLabel(pendingDefaultValue.label)] : undefined,
            onConfirm: handleDefaultValueConfirm,
            onDismiss: handleDefaultValueDismiss
        }
    };
}
